/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'

import { EuiCallOut, EuiFlexGroup, EuiIcon, EuiLink, EuiSkeletonText, EuiText } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { AnyProject } from '@modules/project-api/types'
import type { LinkedCandidateProject } from '@modules/project-user-api/v1/types'
import type { ProjectID, ProjectType } from '@modules/ui-types/projects'
import { MAX_LINKED_PROJECTS } from '@modules/project-components/ProjectCps/ProjectCpsPage'
import ProjectsTable from '@modules/project-components/ProjectCps/ProjectsTable'
import { useProjectCpsContext } from '@modules/project-components/ProjectCps/ProjectCpsContext'

import MaxLinkedProjectsCallout from './MaxLinkedProjectsCallout'

const mapCandidateToProject = (candidate: LinkedCandidateProject): AnyProject =>
  ({
    id: candidate.id,
    name: candidate.name,
    alias: candidate.alias,
    type: candidate.type,
    region_id: candidate.region,
    metadata: {
      created_at: '',
      created_by: '',
      organization_id: '',
      tags: candidate.tags,
    },
  }) as AnyProject

const AllProjectsLinkedCallout: React.FunctionComponent = () => (
  <EuiCallOut color='warning'>
    <FormattedMessage
      id='cps.selectProjects.allProjectsLinked'
      defaultMessage="You've already linked all available projects."
    />
  </EuiCallOut>
)

type Props = {
  currentProjectId: ProjectID
  currentProjectType: ProjectType
  linkedProjectIds: ProjectID[]
  selectedProjects: AnyProject[]
  onSelectionChange: (projects: AnyProject[]) => void
}

const SelectProjectsStep: React.FunctionComponent<Props> = ({
  currentProjectId,
  currentProjectType,
  linkedProjectIds,
  selectedProjects,
  onSelectionChange,
}) => {
  const { formatMessage } = useIntl()
  const { useGetLinkCandidatesQuery } = useProjectCpsContext()

  // Fetch link candidates using the new API
  const linkCandidatesQuery = useGetLinkCandidatesQuery(currentProjectType, currentProjectId)

  const isLoading = linkCandidatesQuery.isLoading

  // Map link candidates to AnyProject format
  const availableProjects = useMemo(
    () => linkCandidatesQuery.data?.items.map(mapCandidateToProject) ?? [],
    [linkCandidatesQuery.data],
  )

  // Build sets for efficient lookup
  const selectedProjectIds = useMemo(
    () => new Set(selectedProjects.map((p) => p.id)),
    [selectedProjects],
  )

  const linkedProjectIdsSet = useMemo(() => new Set(linkedProjectIds), [linkedProjectIds])

  // Get only selectable projects (non-linked)
  const selectableProjects = useMemo(
    () => availableProjects.filter((project) => !linkedProjectIdsSet.has(project.id)),
    [availableProjects, linkedProjectIdsSet],
  )

  const handleSelectAll = useCallback(() => {
    const maxToSelect = MAX_LINKED_PROJECTS - linkedProjectIds.length
    const projectsToSelect = selectableProjects.slice(0, maxToSelect)
    onSelectionChange(projectsToSelect)
  }, [linkedProjectIds.length, selectableProjects, onSelectionChange])

  const handleSelectNone = useCallback(() => {
    onSelectionChange([])
  }, [onSelectionChange])

  const totalLinkedCount = linkedProjectIds.length + selectedProjects.length
  const hasReachedMaxLinkedProjects = totalLinkedCount > MAX_LINKED_PROJECTS

  // Combine selected and linked projects for display in the table
  const allSelectedProjects = useMemo(
    () =>
      availableProjects.filter(
        (project) => selectedProjectIds.has(project.id) || linkedProjectIdsSet.has(project.id),
      ),
    [availableProjects, selectedProjectIds, linkedProjectIdsSet],
  )

  const handleSelectionChange = useCallback(
    (selected: AnyProject[]) => {
      // Only pass non-linked projects to the parent
      const nonLinkedSelected = selected.filter((project) => !linkedProjectIdsSet.has(project.id))

      // Only call parent's onSelectionChange if the selection actually changed
      const currentSelectedIds = selectedProjects
        .map((p) => p.id)
        .sort()
        .join(',')
      const newSelectedIds = nonLinkedSelected
        .map((p) => p.id)
        .sort()
        .join(',')

      if (currentSelectedIds !== newSelectedIds) {
        onSelectionChange(nonLinkedSelected)
      }
    },
    [linkedProjectIdsSet, selectedProjects, onSelectionChange],
  )

  const isProjectSelectable = useCallback(
    (project: AnyProject) => {
      const isLinked = linkedProjectIdsSet.has(project.id)

      if (isLinked) {
        return false
      }

      // If already selected, allow deselection
      const isSelected = selectedProjectIds.has(project.id)

      if (isSelected) {
        return true
      }

      // If not selected and we've reached the max, don't allow selection
      return !hasReachedMaxLinkedProjects
    },
    [linkedProjectIdsSet, selectedProjectIds, hasReachedMaxLinkedProjects],
  )

  const getSelectableMessage = useCallback(
    (selectable: boolean, project: AnyProject) => {
      const isLinked = linkedProjectIdsSet.has(project.id)

      if (isLinked) {
        return formatMessage({
          id: 'linkedProjects.selectableMessage.alreadyIncluded',
          defaultMessage: 'This project is already included',
        })
      }

      return selectable
        ? formatMessage({
            id: 'linkedProjects.selectableMessage.select',
            defaultMessage: 'Select this project',
          })
        : formatMessage({
            id: 'linkedProjects.selectableMessage.maxReached',
            defaultMessage: 'Maximum number of linked projects reached',
          })
    },
    [linkedProjectIdsSet, formatMessage],
  )

  const selection = useMemo(
    () => ({
      selectable: isProjectSelectable,
      selectableMessage: getSelectableMessage,
      onSelectionChange: handleSelectionChange,
      selected: allSelectedProjects,
    }),
    [isProjectSelectable, getSelectableMessage, handleSelectionChange, allSelectedProjects],
  )

  const rowProps = useCallback(
    (project: AnyProject) => {
      const isLinked = linkedProjectIdsSet.has(project.id)

      if (isLinked) {
        return {
          style: {
            opacity: 0.6,
            cursor: 'not-allowed',
          },
        }
      }

      return {}
    },
    [linkedProjectIdsSet],
  )

  if (isLoading) {
    return <EuiSkeletonText />
  }

  let headerContent: React.ReactNode = null

  if (hasReachedMaxLinkedProjects) {
    headerContent = (
      <MaxLinkedProjectsCallout
        maxLinkedProjects={MAX_LINKED_PROJECTS}
        currentProjectType={currentProjectType}
        currentProjectId={currentProjectId}
      />
    )
  } else if (availableProjects.length > 0 && selectableProjects.length === 0) {
    headerContent = <AllProjectsLinkedCallout />
  }

  return (
    <ProjectsTable
      items={availableProjects}
      selectedProjects={selectedProjects}
      onSelectionChange={handleSelectionChange}
      onSelectAll={handleSelectAll}
      onSelectNone={handleSelectNone}
      selection={selection}
      rowProps={rowProps}
      showSearch={true}
      showingTextType='default'
      showSelectAllNone={false}
      headerContent={headerContent}
      rightSideNote={
        <EuiFlexGroup gutterSize='xs' justifyContent='flexEnd'>
          <EuiIcon type='warning' size='m' />
          <EuiText size='s' color='subdued' css={{ minHeight: '32px' }}>
            <FormattedMessage
              id='linkedProjects.compatibilityWarning'
              defaultMessage='Some projects might not appear in this list. {learnMoreLink}'
              values={{
                learnMoreLink: (
                  // TODO: https://github.com/elastic/cloud-ui/issues/952
                  <EuiLink
                    external={true}
                    href='https://www.elastic.co/TBD'
                    target='_target'
                    data-test-subj='cps-selectProjectsStep-learnMoreLink'
                  >
                    Learn more
                  </EuiLink>
                ),
              }}
            />
          </EuiText>
        </EuiFlexGroup>
      }
    />
  )
}

export default SelectProjectsStep
